import { useState } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { createContact } from "@/lib/api";

const labelCls = "eyebrow !text-[0.6rem] text-smoke block mb-1 mt-8";

export default function Contact() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    company: "",
    message: "",
  });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      await createContact(form);
      setState("done");
    } catch (err) {
      setState("idle");
      setError("Something went wrong sending your note. Please try again.");
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16 md:pb-20">
        <Reveal><Eyebrow className="mb-6">Est. United Kingdom — Contact</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight max-w-4xl" data-testid="contact-headline">
            Join Our <span className="italic text-green">Hat Community</span>
          </h1>
        </Reveal>
        <Reveal delay={0.2} className="mt-8 max-w-xl">
          <p className="text-smoke leading-relaxed">
            Questions about the collection, sizing, wholesale or a custom
            commission — we read every note ourselves, and we reply within one
            business day.
          </p>
        </Reveal>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28 md:pb-40 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="border-t border-line pt-10 space-y-12">
              <div>
                <p className="eyebrow !text-[0.6rem] text-smoke mb-4">Write to us</p>
                <a
                  href="mailto:Capstonehatsltd@gmail.com"
                  data-testid="contact-email-link"
                  className="group inline-flex items-start gap-3 font-serif text-2xl md:text-3xl tracking-tight hover:text-green transition-colors duration-300 break-all"
                >
                  Capstonehatsltd@gmail.com
                  <ArrowUpRight size={20} className="shrink-0 mt-1 text-honey group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                </a>
              </div>
              <div>
                <p className="eyebrow !text-[0.6rem] text-smoke mb-4">Call the factory</p>
                <a
                  href="tel:07853180424"
                  data-testid="contact-phone-link"
                  className="group inline-flex items-center gap-3 font-serif text-2xl md:text-3xl tracking-tight hover:text-green transition-colors duration-300"
                >
                  07853 180 424
                  <ArrowUpRight size={20} className="text-honey group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                </a>
              </div>
              <div className="bg-cream border border-line p-8">
                <p className="eyebrow !text-[0.6rem] text-honey mb-3">A note on manners</p>
                <p className="font-serif italic text-lg leading-relaxed text-smoke">
                  "Every enquiry is answered by a person, not a queue. It has
                  always been this way."
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            {state === "done" ? (
              <div className="border border-line bg-cream px-10 md:px-16 py-20" data-testid="contact-success">
                <span className="inline-flex items-center justify-center w-12 h-12 border border-green text-green mb-8">
                  <Check size={20} strokeWidth={1.5} />
                </span>
                <p className="eyebrow text-honey mb-5">Your note has been received</p>
                <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Thank you.</h2>
                <p className="mt-6 text-smoke leading-relaxed max-w-md">
                  It's a pleasure to hear from you. We'll reply within one
                  business day — usually sooner.
                </p>
                <p className="mt-10 font-serif italic text-xl text-green">
                  — Capstone Hats LTD
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-line p-8 md:p-12" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-x-8">
                  <div>
                    <label className={labelCls}>First name *</label>
                    <input data-testid="contact-first-name" className="field" required value={form.first_name} onChange={set("first_name")} placeholder="Edward" />
                  </div>
                  <div>
                    <label className={labelCls}>Last name *</label>
                    <input data-testid="contact-last-name" className="field" required value={form.last_name} onChange={set("last_name")} placeholder="Ashworth" />
                  </div>
                </div>
                <label className={labelCls}>Email *</label>
                <input data-testid="contact-email-input" className="field" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.co.uk" />
                <label className={labelCls}>Company name</label>
                <input data-testid="contact-company-input" className="field" value={form.company} onChange={set("company")} placeholder="Optional" />
                <label className={labelCls}>General inquiries *</label>
                <textarea
                  data-testid="contact-message-input"
                  className="field resize-none"
                  rows={5}
                  required
                  value={form.message}
                  onChange={set("message")}
                  placeholder="How can we help?"
                />
                {error && (
                  <p data-testid="contact-error" className="mt-6 text-sm text-honey italic font-serif">{error}</p>
                )}
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={state === "sending"}
                  className="eyebrow !text-[0.625rem] w-full mt-12 bg-green text-ivory py-5 hover:bg-ink transition-colors duration-300 disabled:opacity-50"
                >
                  {state === "sending" ? "Sending…" : "Send your note"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
